import { Extension } from '@tiptap/core'
import { Plugin } from '@tiptap/pm/state'
import Suggestion from '@tiptap/suggestion'
import { ActionCommandAutocompleteItem } from './actionCommandAutocompleteSource'
import { createActionCommandSuggestion } from './ActionCommandSuggestion'
import { exactActionCommandBeforeCursor } from './exactActionCommandBeforeCursor'
import { rangeIsInsideCodeLiteral } from './codeLiteralContext'

export interface ActionCommandSuggestionExtensionOptions {
    getActionCommands: () => ActionCommandAutocompleteItem[]
    getDefaultActionTokenAttrs?: (
        tag: string,
        item?: ActionCommandAutocompleteItem,
    ) => Record<string, unknown> | null | undefined
    usePreviousTextAsDefaultPrompt?: boolean
    wrapPreviousTextAsPrompt?: boolean
}

export const ActionCommandSuggestionExtension =
    Extension.create<ActionCommandSuggestionExtensionOptions>({
        name: 'actionCommandSuggestion',

        addOptions() {
            return {
                getActionCommands: () => [],
                getDefaultActionTokenAttrs: undefined,
                usePreviousTextAsDefaultPrompt: false,
                wrapPreviousTextAsPrompt: false,
            }
        },

        addProseMirrorPlugins() {
            const editor = this.editor
            const options = this.options
            const actionCommandSuggestion = createActionCommandSuggestion(
                options.getActionCommands,
                options.getDefaultActionTokenAttrs,
                {
                    usePreviousTextAsDefaultPrompt: options.usePreviousTextAsDefaultPrompt === true,
                    wrapPreviousTextAsPrompt: options.wrapPreviousTextAsPrompt === true,
                },
            )
            const exactMatchSpacePlugin = new Plugin({
                props: {
                    handleKeyDown(_view, event) {
                        if (event.key !== ' ' && event.key !== 'Spacebar') return false

                        const { state } = editor
                        const { selection } = state
                        if (!selection.empty) return false

                        for (let depth = selection.$from.depth; depth > 0; depth--) {
                            if (selection.$from.node(depth).type.name === 'actionTag') return false
                        }

                        const selectionPos = selection.from
                        const textBeforeCursor = state.doc.textBetween(
                            Math.max(0, selectionPos - 80),
                            selectionPos,
                            '',
                            '',
                        )
                        const exactMatch = exactActionCommandBeforeCursor(
                            textBeforeCursor,
                            options.getActionCommands?.() || [],
                        )
                        if (!exactMatch) return false

                        const commandRange = {
                            from: selectionPos - exactMatch.rawTag.length - 1,
                            to: selectionPos,
                        }
                        if (rangeIsInsideCodeLiteral(state, commandRange)) return false

                        event.preventDefault()
                        actionCommandSuggestion.command({
                            editor,
                            range: commandRange,
                            props: {
                                ...exactMatch.item,
                                focusPromptOnInsert: true,
                            },
                        })
                        return true
                    },
                },
            })

            return [
                exactMatchSpacePlugin,
                Suggestion({
                    editor,
                    ...actionCommandSuggestion,
                }),
            ]
        },
    })

export default ActionCommandSuggestionExtension
